import React, { useEffect, useState } from 'react';
import Text from 'components/_UI/Text';
import styled, { keyframes } from 'styled-components';
import { Column } from 'styled/alignment/Column';
import { Row } from 'styled/alignment/Row';
import breakpoints from 'theme/breakpoints';
import { FullyCentered } from 'styled/alignment/Center';
import userApi from 'api/requests/user';
import Loader from 'components/_UI/Loader';
import Button from 'components/_UI/Button';
import colors from 'theme/colors';
import EventCardSkeleton from 'components/commons/skeletons/EventCardSkeleton';
import EventCardV2 from '../card/EventCardV2';
import { useFavoritedEvents } from 'context/events/FavoritedEvents';

const PER_PAGE = 9;

export default function EventsFavoritedV2({ onEventClick, onLoadEmpty }) {
    const [loading, setLoading] = useState(true);
    const { favoritedEvents, setFavoritedEvents } = useFavoritedEvents();
    const [expanded, setExpanded] = useState(false);
    const [expandedItems, setExpandedItems] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [loadingMore, setLoadingMore] = useState(false);

    useEffect(() => {
        if (favoritedEvents.length > 0) {
            // Context already has data (e.g. from optimistic add) — skip API fetch
            setLoading(false);
        } else {
            getFavoritedEvents();
        }
    }, []);

    useEffect(() => {
        if (!loading && favoritedEvents.length === 0) onLoadEmpty?.();
    }, [loading, favoritedEvents.length]);

    async function getFavoritedEvents() {
        setLoading(true);
        try {
            const { data: res } = await userApi.favoritedEvents(1, PER_PAGE);
            const items = res?.data || [];
            setFavoritedEvents(items);
            setCurrentPage(res?.meta?.current_page || 1);
            setLastPage(res?.meta?.last_page || 1);
        } catch (error) {
            setFavoritedEvents([]);
        } finally {
            setLoading(false);
        }
    }

    function handleExpandAll() {
        setExpanded(true);
        setExpandedItems(favoritedEvents || []);
    }

    async function loadMore() {
        if (loadingMore || currentPage >= lastPage) return;
        setLoadingMore(true);
        try {
            const nextPage = currentPage + 1;
            const { data: res } = await userApi.favoritedEvents(nextPage, PER_PAGE);
            setExpandedItems(prev => [...prev, ...(res?.data || [])]);
            setCurrentPage(res?.meta?.current_page || nextPage);
        } catch (error) {
            // silent fail
        } finally {
            setLoadingMore(false);
        }
    }

    const displayedItems = expanded ? expandedItems : favoritedEvents;
    const hasMore = currentPage < lastPage;
    const showVerTodos = !expanded && lastPage > 1;
    const displayCount = favoritedEvents.length;

    return (
        <FavoritedContainer>
            {loading ? (
                <SkeletonContainer>
                    {[1, 2, 3, 4, 5, 6].map((_, i) => (
                        <EventCardSkeleton key={i} />
                    ))}
                </SkeletonContainer>
            ) : favoritedEvents.length === 0 ? null : (
                <>
                    <Row>
                        <Text>
                            {'Você tem'}
                        </Text>
                        <Text type='bold' color={colors.secondaryPurple}>
                            &nbsp;{displayCount}
                        </Text>
                        <Text>
                            &nbsp;{displayCount === 1 ? 'evento favorito' : 'eventos favoritos'}
                        </Text>
                    </Row>

                    <CardsContainer>
                        {displayedItems?.map((event, index) => (
                            <AnimatedCard key={event.slug} $index={index}>
                                <EventCardV2
                                    whiteBackground={true}
                                    data={event}
                                    onEventClick={() => onEventClick(event)}
                                />
                            </AnimatedCard>
                        ))}
                    </CardsContainer>

                    <DesktopPagination>
                        {showVerTodos && (
                            <FullyCentered width='100%' marginTop='15px'>
                                <Column width='auto'>
                                    <Button category='minimal' onClick={handleExpandAll}>
                                        <Text paddingBottom={0} type='bold' name='normal' color={colors.secondaryPurple}>
                                            Ver todos os favoritos
                                        </Text>
                                    </Button>
                                </Column>
                            </FullyCentered>
                        )}

                        {expanded && (
                            <FullyCentered marginTop='15px'>
                                <Row width='auto' verticalCenter gap='8px'>
                                    {hasMore && (
                                        loadingMore ? (
                                            <Loader />
                                        ) : (
                                            <Button category='minimal' onClick={loadMore}>
                                                <Text paddingBottom={0} type='bold' name='normal' color={colors.secondaryPurple}>
                                                    Ver mais
                                                </Text>
                                            </Button>
                                        )
                                    )}
                                    <Button category='minimal' onClick={() => {
                                        setExpanded(false);
                                        setExpandedItems([]);
                                    }}>
                                        <Text paddingBottom={0} name='small' color={colors.nightLight} style={{ whiteSpace: 'nowrap' }}>
                                            Ver menos
                                        </Text>
                                    </Button>
                                </Row>
                            </FullyCentered>
                        )}
                    </DesktopPagination>

                    <MobileVerMais>
                        {(showVerTodos || (expanded && hasMore)) && (
                            loadingMore ? (
                                <Loader />
                            ) : (
                                <Button category='minimal' onClick={() => {
                                    if (!expanded) handleExpandAll();
                                    loadMore();
                                }}>
                                    <Text paddingBottom={0} type='bold' name='normal' color={colors.secondaryPurple}>
                                        Ver mais
                                    </Text>
                                </Button>
                            )
                        )}
                    </MobileVerMais>
                </>
            )}
        </FavoritedContainer>
    );
}

const fadeInUp = keyframes`
    from {
        opacity: 0;
        transform: translateY(16px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

const AnimatedCard = styled.div`
    width: 100%;
    animation: ${fadeInUp} 0.4s cubic-bezier(0.22, 1, 0.36, 1) both;
    animation-delay: ${({ $index }) => Math.min($index * 50, 300)}ms;
`;

const FavoritedContainer = styled(Column)`
    margin-top: 16px;
`;

const CardsContainer = styled(Row)`
    width: 100%;
    flex-wrap: nowrap;
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    padding-bottom: 8px;

    & > div {
        min-width: 75vw;
        width: 75vw;
        flex-shrink: 0;
        margin-left: 12px;
    }

    & > div:first-child {
        margin-left: 0;
    }

    @media screen and (min-width: ${breakpoints.lg}) {
        flex-wrap: wrap;
        overflow-x: unset;
        overflow-y: hidden;
        padding-bottom: 0;

        & > div {
            width: 31%;
            min-width: unset;
            margin-left: 1.5%;
        }

        & > div:nth-child(3n+1) {
            width: 31%;
            margin-left: 0 !important;
        }

        & > div:first-child {
            width: 31%;
            margin-left: 0 !important;
        }
    }
`;

const SkeletonContainer = styled(Row)`
    margin-top: 16px;
    flex-wrap: nowrap;
    overflow-x: hidden;

    & > div {
        min-width: 75vw;
        width: 75vw;
        flex-shrink: 0;
        margin-left: 12px;
    }

    & > div:first-child {
        margin-left: 0;
    }

    @media screen and (min-width: ${breakpoints.lg}) {
        flex-wrap: wrap;
        overflow-x: unset;
        margin-top: 30px;

        & > div {
            width: 31.5%;
            min-width: unset;
            margin-left: 1.5%;
            margin-top: 20px;
            & > div {
                width: 100%;
            }
        }

        & > div:first-child {
            margin-left: 1.5%;
        }
    }
`;

const DesktopPagination = styled.div`
    display: none;

    @media screen and (min-width: ${breakpoints.lg}) {
        display: block;
    }
`;

const MobileVerMais = styled(FullyCentered)`
    margin-top: 12px;

    @media screen and (min-width: ${breakpoints.lg}) {
        display: none;
    }
`;
